import type { AxiosInstance, AxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import { AxiosError } from 'axios';
import _ from 'scbk-services/utils/lodash-extension.ts';

import { loadingManager } from 'scbk-services/loading';
import { buildSecureWrappedRequest } from 'scbk-services/shared-context';

import { EventBus, EventType } from 'scbk-services/event-bus/bus/event-bus';
import { RequestType, RestResponseStatus } from 'scbk-services/http/constants';
import { AxiosInstanceManager } from 'scbk-services/http/managers/axios-instance-manager.ts';
import { useRestHeaderStore } from 'scbk-services/http/stores/restHeaderStore.ts';
import { checkRestResponse } from 'scbk-services/http/utils/rest-response-handler.ts';
import { useSecureContextStore } from 'scbk-services/shared-context/stores/secureContextStore';
import type {
    CommonRestResponse,
    CustomAxiosRequestConfig,
} from 'scbk-services/types/http/axios-extension';
import type { RestResponse } from 'scbk-services/types/http/rest-response';

import { addJwtInterceptors } from '../jwt';

class RestService {
    private static instance: RestService | null = null;
    private axiosClient!: AxiosInstance;

    // 2026.08.12 권태민 기호출 api abort를 위한 소스 수정 : bsoi는 servlet 위주라서 미실시
    private static controllers = new Set<AbortController>();

    constructor(baseUrl: string, jwtUseMode: string = 'NONE') {
        if (RestService.instance) {
            RestService.instance.axiosClient = AxiosInstanceManager.getCommonAxiosInstance(baseUrl);
            return RestService.instance;
        }

        this.axiosClient = AxiosInstanceManager.getCommonAxiosInstance(baseUrl);
        this.addInterceptors();

        if ('KONG' === jwtUseMode) {
            // JWT 토큰 처리를 위한 인터셉터 등록
            addJwtInterceptors(this.axiosClient, { jwtUseMode });
        }

        RestService.instance = this;
    }

    private addInterceptors = () => {
        //
        this.axiosClient.interceptors.request.use(
            (config: CustomAxiosRequestConfig) => {
                if (!_.isEqual(config.requestType, RequestType.REST)) {
                    return config;
                }

                console.log(`${config.url}, skipLoading=${config.skipLoading}`);

                if (!config.skipLoading) {
                    //로딩바 노출 이벤트 발행
                    loadingManager.open();
                }
                return config;
            },
            (error: AxiosError) => {
                loadingManager.close();
                return Promise.reject(error);
            },
        );

        this.axiosClient.interceptors.request.use(
            (config: CustomAxiosRequestConfig) => {
                if (_.isEqual(config.requestType, RequestType.REST)) {
                    const {
                        customBaseUrl,
                        headers: originalHeaders,
                        useForceCheckCode = false,
                        forceCheckCode = '',
                    } = config;

                    let contextHeader = useRestHeaderStore.getState().getRestRequestHeader();

                    //------------------------------------------------
                    // [2026.06.18 최성돈] forceCheckCode 처리
                    //------------------------------------------------
                    if (useForceCheckCode && forceCheckCode !== '') {
                        contextHeader = {
                            ...contextHeader,
                            'x-force-check-code': forceCheckCode,
                        };
                    }

                    const headers = _.merge({}, contextHeader, originalHeaders);
                    const omittedHeaders = _.omitBy(headers, _.isUndefined) as AxiosRequestHeaders;

                    if (_.isNotNil(customBaseUrl)) {
                        config.baseURL = config.customBaseUrl;
                    }

                    config.headers = omittedHeaders;
                }
                return config;
            },
            (error: AxiosError) => Promise.reject(error),
        );

        //보안영역 요청 인터셉터 추가
        //SecureContext 데이터가 있는경우 요청데이터를 공통부/개별부 형태로 변환한다.
        this.axiosClient.interceptors.request.use(
            (config: CustomAxiosRequestConfig) => {
                if (!_.isEqual(config.requestType, RequestType.REST)) {
                    return config;
                }

                const secureContext = useSecureContextStore.getState().getSecureContext();

                //useSecureContext === false 인 경우 secureContext 처리를 하지 않는다.
                if (
                    !config.useSecureContext &&
                    !(
                        !_.isEmpty(secureContext?.keypad?.extE2EPlainMap) ||
                        !_.isEmpty(secureContext?.subKeypad?.extE2EPlainMap)
                    )
                ) {
                    return config;
                }

                console.log('### secureContext : ', secureContext);

                const data = config.data;
                if (_.isNil(data)) {
                    return config;
                }

                config.data = buildSecureWrappedRequest(data, secureContext);

                return config;
            },
            (error: AxiosError) => Promise.reject(error),
        );

        // 세션타이머 처리
        this.axiosClient.interceptors.request.use(
            (config: CustomAxiosRequestConfig) => {
                //[2026.04.17 최성돈] 세션 타이머 reset 처리 이벤트로 변경
                EventBus.getInstance().emit(EventType.ResetSessionTimer);
                return config;
            },
            (error: AxiosError) => Promise.reject(error),
        );

        //로딩바 처리
        this.axiosClient.interceptors.response.use(
            async <Data>(response: CommonRestResponse<Data>) => {
                const config = response?.config as CustomAxiosRequestConfig;
                if (_.isNotEmpty(config) && _.isEqual(config.requestType, RequestType.REST)) {
                    //EventBus.getInstance().emit(EventType.HideLoadingBar);
                    //await this.sleep(10_000);
                    if (!config.skipLoading) {
                        loadingManager.close();
                    }
                    return Promise.resolve(response);
                } else {
                    return Promise.resolve(response);
                }
            },
            async (error: AxiosError) => {
                const originalRequest = error.config as CustomAxiosRequestConfig;

                if (_.isEqual(originalRequest?.requestType, RequestType.REST)) {
                    //EventBus.getInstance().emit(EventType.HideLoadingBar);
                    if (!originalRequest.skipLoading) {
                        loadingManager.close();
                    }
                }
                return Promise.reject(error);
            },
        );

        //secure context reset 처리 추가
        this.axiosClient.interceptors.response.use(
            <Data>(response: CommonRestResponse<Data>) => {
                const config = response?.config as CustomAxiosRequestConfig;

                if (_.isNotEmpty(config) && _.isEqual(config.requestType, RequestType.REST)) {
                    //정상/오류 응답여부 체크
                    checkRestResponse(response);

                    // 원본 secureContext
                    const secureContext = {
                        ...useSecureContextStore.getState().getSecureContext(),
                    };

                    //[2026.04.10 최성돈]
                    //정상 응답인 경우, SecureContext reset 처리
                    //요청을 보낼 시 reset 처리 수행하였으나.. 오류응답으로 오는경우 동일 거래를 수행해야하는 문제가 발생을 하여
                    //정상 응답인 경우에만 reset 하도록 수정

                    if (config.useSecureContext) {
                        // requestWithSecure 호출시 삭제
                        useSecureContextStore.getState().resetOthersContext();

                        if (secureContext?.keypad || !_.isEmpty(secureContext?.keypad)) {
                            // 확장 E2E가 없을때, 확장 E2E를 제외한 keypad secureContext 삭제
                            useSecureContextStore.getState().resetKeypadContext();
                        }

                        if (secureContext?.subKeypad || !_.isEmpty(secureContext?.subKeypad)) {
                            // 확장 E2E가 없을때, 확장 E2E를 제외한 subKeypad secureContext 삭제
                            useSecureContextStore.getState().resetSubKeypadContext();
                        }
                    }

                    console.log(
                        '### response after secureContext : ',
                        useSecureContextStore.getState(),
                    );

                    return Promise.resolve(response);
                } else {
                    return Promise.resolve(response);
                }
            },
            async (error: AxiosError) => {
                const originalRequest = error.config as CustomAxiosRequestConfig;

                if (_.isEqual(originalRequest?.requestType, RequestType.REST)) {
                    if (error.response?.status === 403 && originalRequest.authenticationPromise) {
                        const responseData = error.response.data as RestResponse.Data<void>;

                        if (
                            _.isEqual(responseData?.status, RestResponseStatus.FAILURE) &&
                            _.isNotEmpty(responseData?.error?.code)
                        ) {
                            return Promise.reject(error);
                        } else {
                            return Promise.reject(error);
                        }
                    } else {
                        //TODO
                        //checkAxiosErrorResponse(error);
                        return Promise.reject(error);
                    }
                }

                return Promise.reject(error);
            },
        );
    };

    private async request<T>(
        method: string,
        url: string,
        data?: unknown,
        config?: AxiosRequestConfig,
    ): Promise<T> {
        // 2026.08.12 권태민 기호출 api abort를 위한 소스 수정 : bsoi는 servlet 위주라서 미실시
        const controller = new AbortController();

        RestService.controllers.add(controller);
        try {
            const response = await this.axiosClient.request({
                method,
                url,
                data,
                ...config,
                signal: controller.signal,
            });

            return response as unknown as T;
        } finally {
            // 2026.08.12 권태민 기호출 api abort를 위한 소스 수정 : bsoi는 servlet 위주라서 미실시
            RestService.controllers.delete(controller);
        }
    }

    public async post<T>(url: string, data: unknown, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>('POST', url, data, config);
    }

    // 2026.08.12 권태민 기호출 api abort를 위한 소스 수정 : bsoi는 servlet 위주라서 미실시
    public static abortAll() {
        console.log('### pending size : ', RestService.controllers.size);
        RestService.controllers.forEach((controller) => {
            console.log('### before  : ', controller.signal.aborted);
            controller.abort();
            console.log('### after  : ', controller.signal.aborted);
        });
        RestService.controllers.clear();
    }
}

export { RestService };
